import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, CreditCard, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import useAuthStore from '@/lib/stores/authStore';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  canClose?: boolean;
}

/**
 * AccountModal - User account management modal
 * 
 * Features:
 * - User profile information
 * - Subscription details and access countdown
 * - Access extension functionality
 * - Cannot be closed when access is expired
 */
export const AccountModal: React.FC<AccountModalProps> = ({ 
  isOpen, 
  onClose, 
  canClose = true 
}) => {
  const { 
    user, 
    userProfile, 
    accessState, 
    loading, 
    error, 
    fetchUserProfile 
  } = useAuthStore();

  const [activeTab, setActiveTab] = useState<'profile' | 'subscription'>('profile');

  // Refresh data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchUserProfile();
    }
  }, [isOpen, user, fetchUserProfile]);

  // Auto-switch to subscription tab if access is expired
  useEffect(() => {
    if (accessState?.isExpired) {
      setActiveTab('subscription');
    }
  }, [accessState?.isExpired]);

  const handleClose = () => {
    if (canClose && !accessState?.isExpired) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ 
          margin: 0, 
          padding: 0, 
          left: 0, 
          right: 0, 
          top: 0, 
          bottom: 0,
          width: '100vw',
          height: '100vh'
        }}
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={handleClose}
          style={{ margin: 0, padding: 0 }}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden mx-4"
          style={{ margin: 16 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Account</h2>
            {canClose && !accessState?.isExpired && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="p-2"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'profile'
                  ? 'text-primary border-b-2 border-primary bg-primary bg-opacity-5'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                activeTab === 'subscription'
                  ? 'text-primary border-b-2 border-primary bg-primary bg-opacity-5'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CreditCard className="w-4 h-4 inline mr-2" />
              Access
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-96 overflow-y-auto">
            {/* Loading State */}
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                  <User className="w-4 h-4 text-white" />
                </div>
                <p className="text-gray-600 text-sm">Loading account details...</p>
              </div>
            ) : (
              <>
                {/* Error State */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                {/* Access Expired Warning */}
                {accessState?.isExpired && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <div>
                        <h3 className="text-red-800 font-medium">Access Expired</h3>
                        <p className="text-red-700 text-sm mt-1">
                          Your access has expired. Please contact support to extend your access.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

            {/* Profile Tab */}
            {activeTab === 'profile' && userProfile && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <Input
                    value={userProfile.displayName || ''}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input
                    value={userProfile.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Member Since
                  </label>
                  <Input
                    value={userProfile.createdAt.toLocaleDateString()}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>
            )}            {/* Subscription Tab */}
            {activeTab === 'subscription' && accessState && userProfile && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {accessState.isExpired ? 'Access Expired On' : 'Access Expires In'}
                  </label>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {accessState.isExpired ? (
                      <span className="text-red-600 font-medium">
                        {accessState.expirationDate.toLocaleDateString()}
                      </span>
                    ) : (
                      <span className={`font-medium ${
                        accessState.daysRemaining < 30 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {accessState.daysRemaining} {accessState.daysRemaining === 1 ? 'day' : 'days'}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Started On
                  </label>
                  <Input
                    value={userProfile.subscription.startDate.toLocaleDateString()}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Footer - Only show sign out if not expired */}
          {!accessState?.isExpired && (
            <div className="border-t border-gray-200 p-6">
              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    const { signOut } = useAuthStore.getState();
                    await signOut();
                    handleClose();
                  } catch (error) {
                    console.error('Error signing out:', error);
                  }
                }}
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Sign Out
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AccountModal;